import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  DeviceEventEmitter,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';

interface CompletionCardProps {
  taskType: string;
  onViewData?: () => void;
  onViewChecklist?: () => void;
  onClose: () => void;
}

export const CompletionCard: React.FC<CompletionCardProps> = ({
  taskType,
  onViewData,
  onViewChecklist,
  onClose,
}) => {
  const handleViewData = () => {
    if (onViewData) {
      // Emit refresh event before closing
      DeviceEventEmitter.emit('refreshChecklist');
      onClose();
      onViewData();
    }
  };

  const handleViewChecklist = () => {
    if (onViewChecklist) {
      // Emit refresh event before navigating
      DeviceEventEmitter.emit('refreshChecklist');
      onClose();
      onViewChecklist();
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.successIcon}>
          <Image
            source={require('../../assets/icons/check.png')}
            style={styles.checkIcon}
          />
        </View>
        <Text style={styles.title}>Task Completed!</Text>
        <Text style={styles.subtitle}>
          You've successfully completed your {taskType.replace(/\s+information$/i, '')} information.
        </Text>
      </View>

      <View style={styles.content}>
        <Text style={styles.description}>
          Your information has been saved and your access permissions have been set.
          You can now:
        </Text>
      </View>

      <View style={styles.actions}>
        {onViewData && (
          <TouchableOpacity
            style={[styles.button, styles.primaryButton]}
            onPress={handleViewData}
          >
            <Image
              source={require('../../assets/icons/Eye.png')}
              style={styles.buttonIcon}
            />
            <Text style={styles.primaryButtonText}>View Data</Text>
          </TouchableOpacity>
        )}

        {onViewChecklist && (
          <TouchableOpacity
            style={[styles.button, styles.secondaryButton]}
            onPress={handleViewChecklist}
          >
            <Image
              source={require('../../assets/icons/tasks.png')}
              style={[styles.buttonIcon, styles.secondaryButtonIcon]}
            />
            <Text style={styles.secondaryButtonText}>View Checklist</Text>
          </TouchableOpacity>
        )}
      </View>

      <TouchableOpacity style={styles.continueButton} onPress={onClose}>
        <Text style={styles.continueButtonText}>Continue with Orion</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(12),
    padding: ms(20),
    marginVertical: ms(8),
    marginHorizontal: 0,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  header: {
    alignItems: 'center',
    marginBottom: ms(20),
    paddingBottom: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#E0E0E0',
  },
  successIcon: {
    width: s(60),
    height: s(60),
    borderRadius: ms(30),
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: ms(12),
  },
  checkIcon: {
    width: s(30),
    height: s(30),
    tintColor: '#FFFFFF',
  },
  title: {
    fontSize: ms(22, 0.3),
    fontWeight: 'bold',
    color: '#333',
    marginBottom: ms(8),
  },
  subtitle: {
    fontSize: ms(14, 0.3),
    color: '#666',
    textAlign: 'center',
    lineHeight: ms(20, 0.3),
  },
  content: {
    marginBottom: ms(20),
  },
  description: {
    fontSize: ms(14, 0.3),
    color: '#666',
    lineHeight: ms(22, 0.3),
    textAlign: 'center',
  },
  actions: {
    gap: ms(12),
    marginBottom: ms(12),
  },
  button: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: ms(14),
    borderRadius: ms(8),
    gap: ms(8),
  },
  primaryButton: {
    backgroundColor: '#29BFC0',
  },
  secondaryButton: {
    backgroundColor: '#F5F5F5',
    borderWidth: ms(1),
    borderColor: '#29BFC0',
  },
  buttonIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#FFFFFF',
  },
  secondaryButtonIcon: {
    tintColor: '#29BFC0',
  },
  primaryButtonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  secondaryButtonText: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#29BFC0',
  },
  continueButton: {
    paddingVertical: ms(12),
    alignItems: 'center',
  },
  continueButtonText: {
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    color: '#666',
  },
});
